import {
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAuth, useT } from '@powerportalspro/react';
import { ExportGridButton, MainGrid, type ViewDefinition } from '@powerportalspro/react-fluent';

// Vertical flex stack with a small gap between the login warning and the
// grid below it — mirrors the Blazor demo's `<FluentStack VerticalGap="10">`.
// `spacingVerticalMNudge` is the Fluent v9 token for 10px.
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
});

// Mirrors the Blazor `MainGridMainDemo`: a standalone `<MainGrid>` over the
// contact table pinned to the same two custom views the Account/Contact
// demos use ("All Contacts" + "My Contacts"). Unlike `<SubGrid>`, MainGrid
// needs no parent `<RecordContext>` — it loads records directly from the
// supplied views, so the view-picker dropdown, search box, paging, and
// column sorting are all exercised against real data here.
//
// Same view ids on both stacks so a user toggling between the Blazor and
// React tabs keeps a consistent dropdown selection. "Active Contacts" needs
// no filter: the contact table's row security already keeps every query to
// the demo's sample contacts and the signed-in user's own. "My Contacts"
// restricts to just the current user's records; anonymous viewers resolve to
// `Guid.Empty`, which matches the Blazor
// `currentUserId?.ToString() ?? Guid.Empty.ToString()` fallback.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.MainGrid.MainGridMainDemo';

const ALL_CONTACTS_VIEW_ID = '28299C6F-EBC0-4206-9E11-A373D4C9891F';
const MY_CONTACTS_VIEW_ID = '25999A64-A871-4200-A38C-22ACD531F1A2';
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

function buildAllContactsView(): ViewDefinition {
  return {
    id: ALL_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName: 'Active Contacts',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <attribute name="birthdate" />
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
      { columnName: 'birthdate', width: 125 },
    ],
  };
}

function buildMyContactsView(currentUserId: string): ViewDefinition {
  return {
    id: MY_CONTACTS_VIEW_ID,
    tableName: 'contact',
    displayName: 'My Contacts',
    fetchXml: `<fetch>
      <entity name="contact">
        <attribute name="emailaddress1" />
        <attribute name="fullname" />
        <attribute name="mobilephone" />
        <attribute name="parentcustomerid" />
        <attribute name="ownerid" />
        <attribute name="ppp_owningportaluserid" />
        <attribute name="ppp_age" />
        <filter type="or">
          <condition attribute="ppp_owningportaluserid" operator="eq" value="${currentUserId}" />
          <condition attribute="ownerid" operator="eq" value="${currentUserId}" />
        </filter>
        <order attribute="fullname" />
      </entity>
    </fetch>`,
    columns: [
      { columnName: 'fullname', width: 200 },
      { columnName: 'mobilephone', width: 200 },
      { columnName: 'emailaddress1', width: 225 },
      { columnName: 'parentcustomerid', width: 200 },
      { columnName: 'ppp_age', width: 100 },
    ],
  };
}

export function MainGridMainSample() {
  const styles = useStyles();
  const auth = useAuth();
  const t = useT();
  const currentUserId =
    auth.status === 'authenticated' ? auth.user.userId ?? EMPTY_GUID : EMPTY_GUID;

  const customViews: ViewDefinition[] = [
    buildAllContactsView(),
    buildMyContactsView(currentUserId),
  ];

  return (
    <div className={styles.root}>
      <MessageBar
        intent="warning"
        layout="multiline"
        style={{ gridTemplateRows: 'auto', minHeight: 'auto' }}
      >
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        minHeight="400px"
        maxHeight="400px"
        pageSize={10}
        customViewDefinitions={customViews}
        viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
        allowFiltering
      >
        <ExportGridButton />
      </MainGrid>
    </div>
  );
}
